import { useUserStore } from '@talon/web/src/context';
import { StorageProperties } from '@talon/web/src/types';
import Storage from '@talon/web/src/utils/storage';
import { Box, Button, Text } from 'grommet';
import { UserExpert } from 'grommet-icons';
import * as jwt from 'jsonwebtoken';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { User } from '@talon/web/lib/api';

/**
 * Shown at the top of every shell (Layout, AdminLayout) whenever the current session was
 * switched to by adminStore.impersonateUser rather than an ordinary login — never hidden behind
 * a menu, so it's always obvious the app isn't showing the admin's own account.
 */
export const ImpersonationBanner = observer(() => {
  const userStore = useUserStore();

  const impersonatorToken: string = Storage.get(StorageProperties.ImpersonatorJwt);

  // Decoded only for the admin's name in the banner text — restoring the session on Exit re-uses
  // the raw token itself, not this decoded copy.
  const impersonator = useMemo(() => {
    if (!impersonatorToken) return undefined;
    return jwt.decode(impersonatorToken, { json: true })?.data as User | undefined;
  }, [impersonatorToken]);

  if (!impersonatorToken) return null;

  const exitImpersonation = () => {
    userStore.applySession(impersonatorToken, impersonator as User);
    Storage.remove(StorageProperties.ImpersonatorJwt);
    // Full reload, not navigate() — the impersonated user's data is still sitting in every other
    // store (projects, notifications, chat channels...); see ImpersonatePanel's startImpersonating
    // for the same reasoning in the other direction.
    window.location.href = '/admin';
  };

  return (
    <Box
      direction="row"
      align="center"
      justify="center"
      gap="small"
      background="status-warning"
      pad={{ vertical: 'xsmall', horizontal: 'medium' }}
    >
      <UserExpert size="18px" />
      <Text size="small">
        Viewing as <strong>{`${userStore.user?.first_name} ${userStore.user?.last_name}`}</strong> (
        {userStore.user?.email})
        {impersonator ? ` — signed in as ${impersonator.first_name} ${impersonator.last_name}` : ''}
      </Text>
      <Button label="Exit" onClick={exitImpersonation} size="small" plain color="dark-1" />
    </Box>
  );
});
